import classNames from 'classnames';
import React from 'react';

import { Label } from '../Label/Label';
import { ValidationError } from '../ValidationError/ValidationError';
import { ValidationErrors } from '../../types';
import styles from './Input.module.scss';

interface Props<T> {
  className?: string;
  disabled?: boolean;
  errors: ValidationErrors;
  icon?: string;
  inputClassName?: string;
  inverted?: boolean;
  hasBorder?: boolean;
  label?: React.ReactNode;
  layout?: 'vertical' | 'horizontal';
  min?: number;
  max?: number;
  name: string;
  maxLength?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: (value: T) => void;
  placeholder?: string;
  required?: boolean;
  size?: 'regular' | 'large';
  type?: 'text' | 'number' | 'password';
  value: T;
  id?: string;
}

export function Input<T extends string | number = string>(props: Props<T>) {
  const {
    className,
    disabled = false,
    errors,
    icon,
    inputClassName,
    inverted = false,
    hasBorder = true,
    label = '',
    layout = 'vertical',
    min,
    max,
    name,
    maxLength,
    onFocus = () => {},
    onBlur = () => {},
    onChange = () => {},
    placeholder = '',
    required = false,
    size = 'regular',
    type = 'text',
    value,
    id,
  } = props;

  return (
    <div
      className={classNames(
        styles.root,
        { [styles.hasIcon]: icon, [styles.inverted]: inverted, [styles.hasBorder]: hasBorder },
        styles[size],
        styles[layout],
        className
      )}
    >
      <Label
        className={styles.label}
        inverted={inverted}
        required={required}
        text={label}
        textClassName={styles.labelText}
      >
        <div className={styles.wrapper}>
          <input
            autoComplete='off'
            className={classNames(styles.input, inputClassName)}
            autoCapitalize="none"
            disabled={disabled}
            name={name}
            maxLength={maxLength}
            onFocus={onFocus}
            onBlur={onBlur}
            id={id}
            inputMode={type === 'number' ? (name === 'weight' ? 'decimal' : 'numeric') : undefined}
            pattern={type === 'number' ? (name === 'weight' ? "[0-9]*[.]?[0-9]*" : "\\d*") : undefined}
            onKeyDown={(e: any) => {
              if (type !== 'number') return;
              const blocked = ['e', 'E', '+', '-'];
              if (blocked.includes(e.key)) {
                e.preventDefault();
              }
              // Block dot for non-weight numeric inputs
              if (name !== 'weight' && e.key === '.') {
                e.preventDefault();
              }
            }}
            onChange={({ currentTarget }) => {
              let value;
              if (type === 'number') {
                const raw = currentTarget.value;
                if (raw === '') {
                  value = '';
                } else if (!Number.isNaN(Number(raw))) {
                  if ((min && Number(raw) <= min) || (max && Number(raw) >= max)) {
                    return;
                  }
                  if(currentTarget.name === 'pickupPhone' || currentTarget.name === 'destinationPhone'){
                    value = raw;
                  }else if(currentTarget.name !== 'weight'){
                    // For non-weight numeric fields, disallow any decimal point input
                    if (!/^\d+$/.test(raw)) {
                      return;
                    }
                    value = Number(raw);
                  }else if(currentTarget.name === 'weight'){
                    // Allow decimal for weight only, validate characters
                    if (!/^\d*\.?\d*$/.test(raw)) {
                      return;
                    }
                    let tempValue = parseFloat(raw);
                    if(tempValue !== 0){
                      if(tempValue < 0.1){
                        value = 0.1;
                      }else{
                        value = raw;
                      }
                    }else{
                      value = raw;
                    }
                  }else{
                    value = raw;
                  }
                } else {
                  value = '';
                }
              } else {
                value = String(currentTarget.value);
              }
              onChange(value as T);
            }}
            placeholder={placeholder}
            type={type === 'password' ? 'password' : 'text'}
            value={value}
          />
          {icon && <i className={classNames(styles.icon, icon)} />}
          <ValidationError className={styles.error} errors={errors} name={name} />
        </div>
      </Label>
    </div>
  );
}
